import { useState } from 'react';
import { ChevronRightIcon, FolderIcon, KeyIcon, InboxIcon } from '@heroicons/react/24/outline';

interface TreeNode {
  id: string;
  name: string;
  type: 'folder' | 'key';
  children?: TreeNode[];
  fullPath: string;
}

interface KeyTreeProps {
  keys: string[];
  onSelectKey: (key: string) => void;
  selectedKey: string | null;
}

export function KeyTree({ keys, onSelectKey, selectedKey }: KeyTreeProps) {
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());

  // 将扁平的键名数组转换为树形结构
  const buildTree = (keyList: string[]): TreeNode[] => {
    const root: TreeNode[] = [];
    const map: Record<string, TreeNode> = {};

    // 处理每个键
    keyList.forEach(key => {
      const parts = key.split(':');
      let currentPath = '';
      
      for (let i = 0; i < parts.length; i++) {
        const part = parts[i];
        const previousPath = currentPath;
        currentPath = currentPath ? `${currentPath}:${part}` : part;
        
        // 如果节点不存在，则创建它
        if (!map[currentPath]) {
          map[currentPath] = {
            id: currentPath,
            name: part,
            type: i === parts.length - 1 ? 'key' : 'folder',
            children: [],
            fullPath: currentPath
          };
          
          // 将节点添加到父节点的子节点中
          if (map[previousPath]) {
            map[previousPath].children!.push(map[currentPath]);
          } else if (i === 0) {
             // 只有当它是第一级时才推入root
             // 但这里逻辑有点问题，如果 parts[0] 重复出现，会多次 push 吗？
             // map检查防止了重复创建对象，但 root push 需要检查
             // 简单的办法：只在创建 map entry 时，如果是 root level，加入 root 数组
             root.push(map[currentPath]);
          }
        }
      }
    });
    
    // 修正 buildTree 逻辑：上面的 root.push 会导致重复
    // 重新写一个更干净的 buildTree 或者是优化现有的
    // 现有的逻辑里 root 构建是在最后遍历 map 完成的，更稳健
    
    // 让我们沿用之前的逻辑，只是为了保险起见重新梳理一下
    const trueRoot: TreeNode[] = [];
    const nodeMap: Record<string, TreeNode> = {};
    
    keyList.forEach(key => {
      const parts = key.split(':');
      let currentId = '';
      
      parts.forEach((part, index) => {
        const parentId = currentId;
        currentId = currentId ? `${currentId}:${part}` : part;
        
        if (!nodeMap[currentId]) {
          const newNode: TreeNode = {
            id: currentId,
            name: part,
            type: index === parts.length - 1 ? 'key' : 'folder',
            children: [],
            fullPath: currentId
          };
          nodeMap[currentId] = newNode;
          
          if (index === 0) {
            trueRoot.push(newNode);
          } else {
            nodeMap[parentId]?.children?.push(newNode);
          }
        }
      });
    });
    
    return trueRoot;
  };

  const toggleNode = (nodeId: string) => {
    setExpandedNodes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(nodeId)) {
        newSet.delete(nodeId);
      } else {
        newSet.add(nodeId);
      }
      return newSet;
    });
  };

  const renderTree = (nodes: TreeNode[]) => {
    return nodes.map(node => {
      const isExpanded = expandedNodes.has(node.id);
      const isSelected = selectedKey === node.fullPath;
      const hasChildren = node.children && node.children.length > 0;
      
      return (
        <div key={node.id} className="relative">
          <div
            className={`flex items-center py-1.5 px-2 mx-1 my-0.5 rounded-lg cursor-pointer transition-all duration-200 group ${
              isSelected 
                ? 'bg-white/10 text-white shadow-inner shadow-white/5' 
                : 'text-white/60 hover:bg-white/5 hover:text-white'
            }`}
            onClick={() => {
              if (node.type === 'key') {
                onSelectKey(node.fullPath);
              } else if (hasChildren) {
                toggleNode(node.id);
              }
            }}
          >
            {/* Indentation/Icon Area */}
            <div className="flex items-center justify-center w-5 h-5 mr-1 shrink-0">
              {hasChildren ? (
                <div 
                  className={`p-0.5 rounded-md hover:bg-white/10 transition-colors ${isExpanded ? 'rotate-90' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleNode(node.id);
                  }}
                >
                  <ChevronRightIcon className="w-3 h-3 text-white/40" />
                </div>
              ) : (
                <div className="w-3 h-3" />
              )}
            </div>
            
            {/* Type Icon */}
            <div className={`mr-2.5 shrink-0 transition-colors ${
              isSelected 
                ? node.type === 'folder' ? 'text-yellow-400' : 'text-sky-400' 
                : node.type === 'folder' ? 'text-yellow-500/40 group-hover:text-yellow-400/80' : 'text-sky-500/40 group-hover:text-sky-400/80'
            }`}>
              {node.type === 'folder' ? (
                <FolderIcon className="w-4 h-4" />
              ) : (
                <KeyIcon className="w-4 h-4" />
              )}
            </div>
            
            {/* Label */}
            <span className={`text-sm truncate font-mono tracking-tight ${isSelected ? 'font-medium' : ''}`}>
              {node.name}
            </span>
          </div>
          
          {/* Children with guide line */}
          {hasChildren && isExpanded && (
            <div className="ml-3.5 pl-3 border-l border-white/5">
              {renderTree(node.children!)}
            </div>
          )}
        </div>
      );
    });
  };

  const treeData = buildTree(keys);

  return (
    <div className="h-full overflow-y-auto custom-scrollbar pr-2 select-none">
      {treeData.length > 0 ? (
        <div className="pb-4">
          {renderTree(treeData)}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-20">
          <div className="p-4 rounded-full bg-white/5 mb-4">
            <InboxIcon className="h-8 w-8 text-white/10" />
          </div>
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/20">暂无数据</p>
        </div>
      )}
    </div>
  );
}